import moment from 'moment'
import _ from 'lodash'
import { format } from 'date-fns'
import Age from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Age'
import Translated from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Translated'
import { Default } from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Default'
import DefaultLabel from '../components/Visualizer/AppBar/CollaborativeIndexations/Labels/DefaultLabel'
import Unspecified from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Unspecified'
import { isEmpty } from './sanityChecks'
import Gregorian from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/DateCalendars/Gregorian'
import Republican from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/DateCalendars/Republican'
import { toRoman } from 'roman-numerals'
import { RepublicanDate } from './date/republicanDate'
import { Numeric } from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Numeric'
import { DepartmentContainer } from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Address/Department'
import { CountryContainer } from '../components/Visualizer/AppBar/CollaborativeIndexations/Fields/Address/Country'
import { GregorianDate } from './date/gregorianDate'
import BirthDateLabel from "../components/Visualizer/AppBar/CollaborativeIndexations/Labels/BirthDateLabel";

export const translateFormFieldLabel = (t, field) => {
    return t(`visualizer:toolbar.left.collaborativeIndexationCreation.fields.labels.${field}`)
}

export const translateFormFieldValue = (t, field, value) => {
    if(moment(value, 'YYYY-MM-DD', true).isValid())
    {
        return format(moment(value).toDate(), t(`visualizer:common.dateFormat`))
    }

    if(value === null || value.length === 0 || typeof value !== 'string')
    {
        return null
    }

    const translationString = `visualizer:toolbar.left.collaborativeIndexationCreation.fields.values.${field}.${value}`
    let translatedValue = t(translationString)
    if(translatedValue !== translationString.replace(/visualizer:/, ''))
    {
        return translatedValue
    }

    return value
}

export const translateFormFieldPlaceholder = (t, field) => {
    return t(`visualizer:toolbar.left.collaborativeIndexationCreation.fields.placeholder.${field}`)
}

export function InvalidStepException(fields) {
    this.fields = fields
}

export const validateFields = fields =>
{
    const invalidFields = []
    Object.entries(fields).forEach(([field, validator]) => {
        if(typeof(validator) !== "function")
        {
            return
        }

        if(! validator())
        {
            invalidFields.push(field)
        }
    })

    if(! _.isEmpty(invalidFields))
    {
        throw new InvalidStepException(invalidFields)
    }
}

export function getComponentFromField(field, value)
{
    if(value === null)
    {
        return Unspecified
    }

    if(field === 'age')
    {
        return Age
    }

    if(['eventDate', 'birthDate'].includes(field) && value.hasOwnProperty('type'))
    {
        if(value.type === 'gregorian')
        {
            return Gregorian
        }

        if(value.type === 'republican')
        {
            return Republican
        }
    }

    if(['birthDepartment', 'homeDepartment'].includes(field))
    {
        return DepartmentContainer
    }

    if(['birthCountry', 'homeCountry'].includes(field))
    {
        return CountryContainer
    }

    if(['eventType', 'relationship', 'gender', 'documentType'].includes(field))
    {
        return Translated
    }

    if(_.isNumber(value))
    {
        return Numeric
    }

    if(_.isString(value) && value.trim().length > 0)
    {
        return Default
    }

    return Unspecified
}

export function getLabelComponentFromField(field)
{
    if(field === 'birthDate' || field === 'birthYear')
    {
        return BirthDateLabel
    }

    return DefaultLabel
}

const gregorianInvalidParts = (dateParts) => {
    const invalidParts = []

    if(! isEmpty(dateParts.day) && ! _.inRange(dateParts.day, 1, GregorianDate.MAX_DAYS + 1))
    {
        invalidParts.push('day')
    }

    if(! isEmpty(dateParts.month) && ! _.inRange(dateParts.month, 1, GregorianDate.MAX_MONTHS + 1 ))
    {
        invalidParts.push('month')
    }

    if(! isEmpty(dateParts.year) && ! _.inRange(dateParts.year, 1, GregorianDate.MAX_YEARS + 1))
    {
        invalidParts.push('year')
    }

    return invalidParts
}

const republicanInvalidParts = (dateParts) => {

    if(! isEmpty(dateParts.day) && ! _.inRange(dateParts.day, 1, RepublicanDate.MAX_DAYS + 1))
    {
        return ['day']
    }

    return []
}

export const missingDateParts = date => {
    let missingParts = []

    if(isEmpty(date.parts.year) && (! isEmpty(date.parts.day) || ! isEmpty(date.parts.month)))
    {
        missingParts.push('year')
    }

    if(isEmpty(date.parts.month) && ! isEmpty(date.parts.day))
    {
        missingParts.push('month')
    }

    return missingParts
}

export const invalidDateParts = date => {
    let invalidParts = []

    if(date === null)
    {
        return invalidParts
    }

    invalidParts = _.concat(invalidParts, missingDateParts(date))

    if(date.type === 'gregorian')
    {
        invalidParts = _.concat(invalidParts, gregorianInvalidParts(date.parts))
    }

    if(date.type === 'republican')
    {
        invalidParts = _.concat(invalidParts, republicanInvalidParts(date.parts))
    }

    return _.uniq(invalidParts)
}

export const isDateValid = (date) => {
    if(! date.hasOwnProperty('parts'))
    {
        return false
    }

    return invalidDateParts(date).length === 0
}

export const formatGregorianDateFromDateParts = (dateParts, translate) =>
{
    const transParameters = {
        year: dateParts.year,
        month: _.padStart(dateParts.month, 2, 0),
        day: _.padStart(dateParts.day, 2, 0)
    }

    if(! isEmpty(dateParts.day))
    {
        return translate('visualizer:toolbar.left.collaborativeIndexationCreation.gregorianForm.formats.yearMonthDay', transParameters)
    }

    if(! isEmpty(dateParts.month))
    {
        return translate('visualizer:toolbar.left.collaborativeIndexationCreation.gregorianForm.formats.yearMonth', transParameters)
    }

    return translate('visualizer:toolbar.left.collaborativeIndexationCreation.gregorianForm.formats.year', transParameters)
}

export const formatRepublicanDateFromDateParts = (dateParts, translate) => {
    let formattedDate = ''

    if(! isEmpty(dateParts.year))
    {
        formattedDate = translate('visualizer:toolbar.left.collaborativeIndexationCreation.republicanForm.formats.year', {year: toRoman(dateParts.year)})
    }

    if(! isEmpty(dateParts.month))
    {
        const monthAsString = RepublicanDate.MONTHS[dateParts.month - 1]
        const translatedMonth = translate(`visualizer:toolbar.left.collaborativeIndexationCreation.republicanForm.months.${monthAsString}`)

        formattedDate = `${translatedMonth} ${formattedDate}`
    }

    if(! isEmpty(dateParts.day))
    {
        formattedDate = `${dateParts.day} ${formattedDate}`
    }

    return formattedDate
}

export const sanitizeMilitaryId = (value) => {
    if(value === '')
    {
        return null
    }

    return Math.max(0, Math.min(999999,
        Number(value)
    ))
}
